import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { useFeatureConfigContext } from '@/contexts/FeatureConfigContext';

interface MaintenanceBannerProps {
  featureId: string;
}

const STORAGE_KEY_PREFIX = 'maintenance-dismissed-';

export const MaintenanceBanner: React.FC<MaintenanceBannerProps> = ({ featureId }) => {
  const storageKey = STORAGE_KEY_PREFIX + featureId;
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  const { configs } = useFeatureConfigContext();
  const config = configs.get(featureId);
  const message = config?.maintenanceMessage || 'This feature is currently undergoing maintenance. Some functionality may be limited.';

  if (dismissed) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
    setDismissed(true);
  };

  return (
    <div className="flex items-start gap-3 px-4 py-3 mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl text-amber-800 dark:text-amber-200">
      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-500" />
      <p className="flex-1 text-sm font-medium">{message}</p>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss maintenance notice"
        className="shrink-0 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
